import { AUDIO_FILE_ISSUES_ENTITY_ID } from '@/composables/audioFileIssuesSchema';
import { PLAYLIST_SWITCHING_ENTITY_ID } from '@/composables/playlistSwitchingSchema';

/**
 * Track Matching has no schema module of its own — it borrows LiveTrack's columns
 * through entityColumnUniverse — so its id lives here with the rest of the
 * page-level registry.
 *
 * Like the other two ids this string is a DURABLE WIRE VALUE. It is stored verbatim
 * in `saved_views.entity` and `favorite_filters.entity`, and the backend keys its
 * page-to-permission map off it. Renaming it orphans every row already saved.
 */
export const TRACK_MATCHING_ENTITY_ID = 'TrackMatching';

export const TRACK_MATCHING_ROUTE = 'track-matching';
export const AUDIO_FILE_ISSUES_ROUTE = 'audio-file-issues';
export const PLAYLIST_SWITCHING_ROUTE = 'playlist-switching';

/**
 * Single source of truth for mapping a route name to a page-level (non-Eloquent)
 * persistent-table entity id. Both {@link usePersistentTable} and the
 * persistentTableStore use this so a new report only needs to be registered
 * once — keeping them in lockstep avoids the store seeding defaults for one
 * entity id while the composable reads columns for another.
 */
export const AUDIO_FILE_ISSUES_ROUTES: ReadonlySet<string> = new Set([
  AUDIO_FILE_ISSUES_ROUTE,
  'audio-file-issues-detail',
]);

/** Everything the app has to be able to say about a page-level scope. */
export interface PageLevelTableEntity {
  /** What to call it in prose, wherever a scope is named to a user. */
  label: string;
  /** Where a saved view for this scope lands. Must exist in the router. */
  routeName: string;
}

/**
 * The page-level scopes, and the only place their facts are written down.
 *
 * Search-page entities get a label and a route from models.json through the entity
 * store. These three have no model behind them, so this is their models.json. The
 * same three-branch `if` chain was copied into the browse dialog, the import page
 * (twice) and the store, and the failure when a copy is missed is silent and
 * user-visible: an imported Playlist Switching view lands on Search under a green
 * success toast, or a dialog subtitle reads "shared for PlaylistSwitching".
 *
 * Adding a report here should be the only edit it needs outside its own schema file.
 */
export const PAGE_LEVEL_TABLE_ENTITIES: Readonly<Record<string, PageLevelTableEntity>> = {
  [TRACK_MATCHING_ENTITY_ID]: { label: 'Track Matching', routeName: TRACK_MATCHING_ROUTE },
  [AUDIO_FILE_ISSUES_ENTITY_ID]: { label: 'Audio File Issues', routeName: AUDIO_FILE_ISSUES_ROUTE },
  [PLAYLIST_SWITCHING_ENTITY_ID]: { label: 'Playlist Switching', routeName: PLAYLIST_SWITCHING_ROUTE },
};

/** Is this entity id a page-level scope rather than a searchable model? */
export function isPageLevelEntityId(entityId: string): boolean {
  return entityId in PAGE_LEVEL_TABLE_ENTITIES;
}

/** The display name for a page-level scope, or null for a searchable model. */
export function pageLevelEntityLabel(entityId: string): string | null {
  return PAGE_LEVEL_TABLE_ENTITIES[entityId]?.label ?? null;
}

/** The route a page-level scope lives at, or null for a searchable model. */
export function pageLevelEntityRouteName(entityId: string): string | null {
  return PAGE_LEVEL_TABLE_ENTITIES[entityId]?.routeName ?? null;
}

/**
 * The page-level entity id for a route, or null if the route isn't a
 * page-level report (callers fall back to their own entity selection).
 */
export function resolvePageLevelEntityId(routeName: string): string | null {
  if (AUDIO_FILE_ISSUES_ROUTES.has(routeName)) {
    return AUDIO_FILE_ISSUES_ENTITY_ID;
  }
  if (routeName === PLAYLIST_SWITCHING_ROUTE) {
    return PLAYLIST_SWITCHING_ENTITY_ID;
  }
  if (routeName === TRACK_MATCHING_ROUTE) {
    return TRACK_MATCHING_ENTITY_ID;
  }
  return null;
}
